import {
  formatZoneTime,
  listZones,
  searchZones,
  SUGGESTED_ZONES,
  zoneCity,
  zoneCityLabel,
  zoneRegion,
} from '@itin/shared'
import { useEffect, useMemo, useState } from 'react'
import { useLocale, useT } from '../lib/i18n.js'

/**
 * Picks a time zone by naming a city and showing the clock there.
 *
 * This replaces a text box that wanted `America/Sao_Paulo` typed correctly.
 * Nobody knows that string, a typo in it is accepted by the browser and
 * rejected by the server, and — worse — a plausible wrong answer is accepted by
 * both. `Europe/London` instead of `Europe/Lisbon` is one letter and one hour.
 *
 * So the control does not ask the advisor to recognise an identifier. It asks
 * her to name a place, and then shows what time it is there. A wrong pick stops
 * being a string she cannot check and becomes a clock reading she can: nobody
 * confirms a Paris trip while the panel says it is four in the morning.
 */
export function TimeZonePicker({
  value,
  onChange,
  name,
  inputId,
}: {
  value: string
  onChange(zone: string): void
  /** Emitted as a hidden input so plain `FormData` submission keeps working. */
  name?: string
  inputId?: string
}) {
  const t = useT()
  const { locale } = useLocale()
  const [query, setQuery] = useState('')

  const zones = useMemo(() => listZones(), [])
  const results = useMemo(
    () => (query.trim() ? searchZones(query, zones).slice(0, 40) : []),
    [query, zones],
  )

  const now = useTickingClock()
  const suggestions = useMemo(
    () => SUGGESTED_ZONES.map((zone) => ({ zone, city: zoneCity(zone), region: zoneRegion(zone) })),
    [],
  )

  const shown = query.trim() ? results : suggestions

  return (
    <div className="flex flex-col gap-2">
      {name && <input type="hidden" name={name} value={value} />}

      {value && (
        // The confirmation, and the reason this control exists. Deliberately
        // above the search box: it is what the advisor should read last before
        // moving on, and what she will glance at when she comes back.
        <p className="rounded-md bg-slate-100 px-3 py-2 text-sm">
          {t('timezone.confirm', {
            city: zoneCityLabel(value, locale),
            time: formatZoneTime(value, locale, now),
          })}
        </p>
      )}

      <input
        id={inputId}
        type="search"
        value={query}
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('timezone.searchPlaceholder')}
        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      />

      {!query.trim() && (
        <p className="text-xs text-slate-500">{t('timezone.suggestionsLabel')}</p>
      )}

      {query.trim() && shown.length === 0 ? (
        <p className="text-xs text-slate-500">{t('timezone.noMatches')}</p>
      ) : (
        <ul
          role="listbox"
          aria-label={t('timezone.listLabel')}
          className="max-h-56 overflow-y-auto rounded-md border border-slate-200"
        >
          {shown.map((choice) => {
            const selected = choice.zone === value
            return (
              <li key={choice.zone}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(choice.zone)
                    setQuery('')
                  }}
                  className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                    selected ? 'bg-brand/10 font-medium' : ''
                  }`}
                >
                  <span className="flex flex-col">
                    <span>{zoneCityLabel(choice.zone, locale)}</span>
                    <span className="text-xs text-slate-500">{choice.region}</span>
                  </span>
                  {/* Every row carries its clock, not just the chosen one — so
                      the difference between two candidates is visible before
                      committing to one of them. */}
                  <span className="tabular-nums text-xs text-slate-500">
                    {formatZoneTime(choice.zone, locale, now)}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/**
 * A clock that advances while the panel is open.
 *
 * Twenty seconds, not a minute: a reading that is up to a minute stale is a
 * reading that disagrees with the phone on the desk, and this component's whole
 * job is to be checkable against the world.
 */
function useTickingClock(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 20_000)
    return () => clearInterval(timer)
  }, [])
  return now
}
